import { useState, useCallback } from 'react';

import type { BuildDocFiltersWithNLResponseDto } from '../../../backend/src/matter/doc/dto/build-doc-filters-with-nl.dto.js';
import type { BuildFactFiltersWithNLResponseDto } from '../../../backend/src/matter/fact/dto/build-fact-filters-with-nl.dto.js';
import { useBuildDocFiltersWithNL } from '@/api/mutations/useBuildDocFiltersWithNL';
import { useBuildFactFiltersWithNL } from '@/api/mutations/useBuildFactFiltersWithNL';
import { DocFilters, FactFilters } from '@/types';

interface UseNaturalLanguageFiltersProps<T extends DocFilters | FactFilters> {
  matterId: string;
  subsetId: string;
  onFiltersChange: (filters: T) => void;
  onSearchChange?: (query: string) => void;
  onKeyFactsToggle?: (active: boolean) => void;
  onDisputedFactsToggle?: (active: boolean) => void;
}

interface UseNaturalLanguageFiltersReturn {
  buildFiltersWithNL: (query: string) => void;
  isProcessing: boolean;
  error: Error | null;
}

export function useDocumentNaturalLanguageFilters(
  props: UseNaturalLanguageFiltersProps<DocFilters>,
): UseNaturalLanguageFiltersReturn {
  const { matterId, subsetId, onFiltersChange, onSearchChange } = props;
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { mutate: buildDocFilters } = useBuildDocFiltersWithNL();

  const handleError = useCallback(
    (err: Error, fallbackQuery?: string) => {
      setError(err);
      if (fallbackQuery && onSearchChange) {
        onSearchChange(fallbackQuery);
      }
      setIsProcessing(false);
    },
    [onSearchChange],
  );

  const buildFiltersWithNL = useCallback(
    (query: string) => {
      setIsProcessing(true);
      setError(null);

      buildDocFilters(
        { matterId, subsetId, query },
        {
          onSuccess: (response: BuildDocFiltersWithNLResponseDto) => {
            try {
              const { searchQuery, ...filters } = response.filters;
              onFiltersChange(filters as DocFilters);

              if (onSearchChange) {
                onSearchChange(searchQuery ?? '');
              }

              setIsProcessing(false);
            } catch (err) {
              handleError(err instanceof Error ? err : new Error('Unknown error'));
            }
          },
          onError: (err: Error) => {
            handleError(err, query);
          },
        },
      );
    },
    [matterId, subsetId, onFiltersChange, onSearchChange, buildDocFilters, handleError],
  );

  return { buildFiltersWithNL, isProcessing, error };
}

export function useFactNaturalLanguageFilters(
  props: UseNaturalLanguageFiltersProps<FactFilters>,
): UseNaturalLanguageFiltersReturn {
  const { matterId, subsetId, onFiltersChange, onSearchChange, onKeyFactsToggle, onDisputedFactsToggle } = props;
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { mutate: buildFactFilters } = useBuildFactFiltersWithNL();

  const handleError = useCallback(
    (err: Error, fallbackQuery?: string) => {
      setError(err);

      if (fallbackQuery && onSearchChange) {
        onSearchChange(fallbackQuery);
      }

      setIsProcessing(false);
    },
    [onSearchChange],
  );

  const buildFiltersWithNL = useCallback(
    (query: string) => {
      setIsProcessing(true);
      setError(null);

      buildFactFilters(
        { matterId, subsetId, query },
        {
          onSuccess: (response: BuildFactFiltersWithNLResponseDto) => {
            try {
              const { searchQuery, keyFacts, disputedFacts, ...otherFilters } = response.filters;

              if (keyFacts && onKeyFactsToggle) {
                onKeyFactsToggle(true);
              }
              if (disputedFacts && onDisputedFactsToggle) {
                onDisputedFactsToggle(true);
              }

              onFiltersChange({
                ...otherFilters,
                keyFacts,
                disputedFacts,
              });

              if (onSearchChange) {
                onSearchChange(searchQuery ?? '');
              }

              setIsProcessing(false);
            } catch (err) {
              handleError(err instanceof Error ? err : new Error('Unknown error'));
            }
          },
          onError: (err: Error) => {
            handleError(err, query);
          },
        },
      );
    },
    [
      matterId,
      subsetId,
      onFiltersChange,
      onSearchChange,
      onKeyFactsToggle,
      onDisputedFactsToggle,
      buildFactFilters,
      handleError,
    ],
  );

  return { buildFiltersWithNL, isProcessing, error };
}
